import type { CSSProperties } from "react";
import { useMemo } from "react";

import { pxToRem } from "@/theme/spacing";
import { zIndex } from "@/theme/zIndex";
import type { DropdownPosition } from "./Cdropdown.types";

interface UseDropdownListStyleInput {
  anchorMeasuredWidth: number | null;
  dropdownPosition: DropdownPosition;
  matchAnchorWidth: boolean;
  width: number | string;
  maxWidth: number | string;
}

/** A bare number means px at every dropdown width seam, matching the `width`
 *  prop's long-standing contract. */
function resolveWidthValue(value: number | string): string {
  return typeof value === "number" ? pxToRem(value) : value;
}

/**
 * The horizontal axis. `matchAnchorWidth` still pins the panel to its anchor —
 * that is a deliberate "as wide as the card" behaviour, not a size guess.
 * Everywhere else the panel is CONTENT-sized between two bounds: `width` is the
 * floor a short menu never drops below, `maxWidth` the ceiling a long label
 * cannot push past (past it the label ellipsizes instead of shoving the icon
 * and the chevron out of the row).
 */
function resolveDropdownSizeStyle(input: {
  matchAnchorWidth: boolean;
  anchorMeasuredWidth: number | null;
  width: number | string;
  maxWidth: number | string;
}): CSSProperties {
  if (input.matchAnchorWidth && input.anchorMeasuredWidth !== null) {
    return { width: `${input.anchorMeasuredWidth}px` };
  }

  return {
    width: "max-content",
    minWidth: resolveWidthValue(input.width),
    maxWidth: resolveWidthValue(input.maxWidth),
  };
}

export function useDropdownListStyle({
  anchorMeasuredWidth,
  dropdownPosition,
  matchAnchorWidth,
  width,
  maxWidth,
}: UseDropdownListStyleInput): CSSProperties {
  return useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      position: "fixed",
      ...resolveDropdownSizeStyle({
        matchAnchorWidth,
        anchorMeasuredWidth,
        width,
        maxWidth,
      }),
      zIndex: zIndex.dropdown,
    };

    if (dropdownPosition.openUpwards && dropdownPosition.bottom !== null) {
      return {
        ...baseStyle,
        left: `${dropdownPosition.left}px`,
        bottom: `${dropdownPosition.bottom}px`,
      };
    }

    return {
      ...baseStyle,
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
    };
  }, [
    anchorMeasuredWidth,
    dropdownPosition,
    matchAnchorWidth,
    maxWidth,
    width,
  ]);
}
